import React, { useState, useEffect, useMemo, useRef, RefObject } from "react";
import clsx from "clsx";

import { type ServerDataSuccess } from "./route";
import { Sheet } from "~/components/Sheet";
import { Sections } from "~/components/Sections/Sections";

import { useRichText } from "~/hooks/useRichText";
import { useSheet } from "~/hooks/useSheet";

import {
  type TableProps,
  Callout,
  SanityImage,
  ShopifyRichText,
  Table,
  TabbedSectionProps,
  TabbedSection,
  IconArchSupport,
  IconCuboidNotch,
  IconHeelCup,
  IconMetatarsalDome,
  IconVintageArrow,
  IconForefootPadding,
  IconThinFootbed,
  IconOrthotics,
  Video,
} from "@bared/ui";
import { useProduct } from "./useProduct";

export interface SizeAndFitProps {
  /** The active tab to display */
  activeTab: "sizeGuide" | "biomechanicalFootbed" | "customisableFit";
  /** The gender of the product */
  gender?: "Mens" | "Womens";
  /** Whether the fit kit is included for the product */
  isFitKitIncluded: ServerDataSuccess["isFitKitIncluded"];
  /** The function to handle the sheet toggle */
  handleSheetToggle: () => void;
  /** Whether the sheet is open */
  isOpen: boolean;
}

export interface SizeAndFitButtonProps
  extends Pick<SizeAndFitProps, "gender" | "isFitKitIncluded"> {}

export function SizeAndFitSheet(props: SizeAndFitProps) {
  const tabs: TabbedSectionProps["tabs"] = [
    { title: "Size Guide", handle: "sizeGuide" },
    ...(props.isFitKitIncluded
      ? [{ title: "Customisable Fit", handle: "customisableFit" }]
      : []),
    { title: "Biomechanical Footbed", handle: "biomechanicalFootbed" },
  ];
  const currentActiveTab =
    tabs.find((tab) => tab.handle === props.activeTab)?.title ?? undefined;

  const [sheetTitle, setSheetTitle] = useState<string | undefined>(
    currentActiveTab,
  );

  const handleTitleChange = (tabHandle: string) => {
    const title =
      tabs.find((tab) => tab.handle === tabHandle)?.title ?? undefined;

    setSheetTitle(title);
  };

  return (
    <Sheet
      isOpen={props.isOpen}
      onClose={props.handleSheetToggle}
      title={sheetTitle}
      eventTitle={currentActiveTab}
    >
      <TabbedSheet {...props} onChange={handleTitleChange} tabs={tabs} />
    </Sheet>
  );
}

function TabbedSheet({
  onChange,
  tabs,
  ...props
}: SizeAndFitProps & {
  onChange: TabbedSectionProps["onChange"];
  tabs: TabbedSectionProps["tabs"];
}) {
  const {
    biomechanicalFootbedSheetContents: biomechanicalFootbed,
    customisableFitSheetContents: customisableFit,
    template: { sheetFooterContent },
    sizeGuideSheetContents: sizeGuide,
  } = useProduct();

  const tabHandles = tabs.map((tab) => tab.handle);
  const { handleTabNavigation, sheetLinkClicks } = useSheet(tabHandles);
  const { parseContent } = useRichText();

  const [activeTab, setActiveTab] = useState<string>(props.activeTab);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const footerContent = parseContent(sheetFooterContent);

  /** Adds a click event listener to all links in the sheet container
   * to determine if the sheet should be closed or not
   */
  useEffect(() => {
    sheetLinkClicks(wrapperRef, props.handleSheetToggle);
  }, [wrapperRef, props.handleSheetToggle]);

  /** Adds a click event listener to elements with 'data-tab-to-navigate' attribute
   * to handle both internal tab navigation and external sheet opening
   */
  useEffect(() => {
    handleTabNavigation(wrapperRef, setActiveTab, onChange);
  }, [wrapperRef, onChange, setActiveTab, handleTabNavigation]);

  return (
    <>
      <TabbedSection
        tabs={tabs}
        stickyTabTitle
        enableTabTitleScroll
        onChange={(tabHandle) => {
          setActiveTab(tabHandle);
          if (wrapperRef.current) {
            wrapperRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          }
          if (onChange) onChange(tabHandle);
        }}
        activeTab={activeTab}
        scrollableTabTitleClassName="scroll-px-midi md:scroll-px-maxi ps-midi md:ps-maxi"
      >
        {(tabHandle) => {
          if ("sizeGuide" === tabHandle)
            return (
              <SizeGuideSheetContent
                wrapperRef={wrapperRef}
                sizeGuide={sizeGuide}
                gender={props.gender}
              />
            );

          if ("customisableFit" === tabHandle)
            return (
              <CustomisableFitSheetContent
                wrapperRef={wrapperRef}
                customisableFit={customisableFit}
              />
            );

          if ("biomechanicalFootbed" === tabHandle)
            return (
              <BiomechanicalFootbedSheetContent
                wrapperRef={wrapperRef}
                biomechanicalFootbed={biomechanicalFootbed}
                gender={props.gender}
              />
            );
        }}
      </TabbedSection>

      {!!footerContent && (
        <div className="border-t border-primary-300 px-midi py-midi md:px-maxi">
          {footerContent}
        </div>
      )}
    </>
  );
}

function SizeGuideSheetContent({
  sizeGuide,
  gender,
  wrapperRef,
}: Pick<SizeAndFitProps, "gender"> & {
  sizeGuide: ServerDataSuccess["sizeGuideSheetContents"];
  wrapperRef: RefObject<HTMLDivElement>;
}) {
  const { parseContent } = useRichText();

  if (!sizeGuide) return null;

  const tableRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const {
    men,
    women,
    summaryMens,
    summaryWomens,
    flexibleSections,
    sizingAdvice,
  } = sizeGuide;

  const sheetContent = useMemo(() => {
    if (gender === "Mens") {
      return {
        summary: summaryMens,
        sizes: men,
        flexibleSections,
        sizingAdvice,
      };
    }

    if (gender === "Womens") {
      return {
        summary: summaryWomens,
        sizes: women,
        flexibleSections,
        sizingAdvice,
      };
    }
  }, [gender]);
  const summary = parseContent(sheetContent?.summary);

  const tableHeaders: TableProps["columns"] = [
    { name: "Bared/EU", id: "bared", isRowHeader: true },
    { name: "US", id: "us_au" },
    { name: "CM", id: "cm" },
    { name: "IN", id: "inch" },
  ];

  const tableCells: TableProps["row"] = sheetContent?.sizes
    ? sheetContent.sizes.map((size, index) => ({
        id: index + 1,
        bared: size.bared,
        us_au: size.us_au,
        cm: size.cm,
        inch: size.inch,
      }))
    : [];

  // Function to update the scroll state of table (whether it's possible to scroll left or right)
  const updateTableScrollState = () => {
    if (tableRef.current) {
      const scrollLeft = Math.round(tableRef.current.scrollLeft);
      const scrollWidth = tableRef.current.scrollWidth;
      const clientWidth = tableRef.current.clientWidth;

      // Can scroll left if scrollLeft > 0
      setCanScrollLeft(scrollLeft > 0);

      // Can scroll right if the total scrollable width exceeds the visible width
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
    }
  };

  const handleScrollRight = () => {
    if (tableRef.current && canScrollRight) {
      tableRef.current.scrollTo({
        left: tableRef.current.scrollLeft + 200,
        behavior: "smooth",
      });

      updateTableScrollState();
    }
  };

  const handleScrollLeft = () => {
    if (tableRef.current && canScrollLeft) {
      tableRef.current.scrollTo({
        left: tableRef.current.scrollLeft - 200,
        behavior: "smooth",
      });

      updateTableScrollState();
    }
  };

  useEffect(() => {
    if (tableRef.current) {
      updateTableScrollState();

      // Add scroll event listener to tableRef to update the scroll state
      tableRef.current.addEventListener("scroll", updateTableScrollState);
    }

    window.addEventListener("resize", updateTableScrollState);

    return () => {
      tableRef.current?.removeEventListener("scroll", updateTableScrollState);
      window.removeEventListener("resize", updateTableScrollState);
    };
  }, [tableRef.current, updateTableScrollState]);

  return (
    <div ref={wrapperRef} className="px-midi md:px-maxi">
      <h3 className="font-display text-h3 leading-h3 lg:text-[32px]">
        Size Guide
      </h3>

      <div className="flex flex-col space-y-mini">
        {!!summary && summary}
        {!!sheetContent?.sizingAdvice && (
          <Callout variant="secondary" size="large" className="size-fit">
            <ShopifyRichText content={sheetContent.sizingAdvice} />
          </Callout>
        )}
      </div>

      <div className="flex flex-col space-y-midi pb-midi pt-mini md:space-y-maxi">
        {tableCells.length > 0 && (
          <div className="relative">
            <TableControls
              scrollRight={handleScrollRight}
              scrollLeft={handleScrollLeft}
              canScrollRight={canScrollRight}
              canScrollLeft={canScrollLeft}
            />
            <Table
              tableHeaderClassName="p-micro text-caption bg-primary-100 font-normal"
              cellClassName="p-micro text-caption"
              columns={tableHeaders}
              row={tableCells}
              orientation="horizontal"
              tableRef={tableRef}
            />
          </div>
        )}

        {!!sheetContent?.flexibleSections && (
          <div className="grid grid-cols-1 gap-micro md:grid-cols-2 md:gap-mini">
            <Sections page={{ content: sheetContent?.flexibleSections }} />
          </div>
        )}
      </div>
    </div>
  );
}

function CustomisableFitSheetContent({
  customisableFit,
  wrapperRef,
}: {
  customisableFit: ServerDataSuccess["customisableFitSheetContents"];
  wrapperRef: RefObject<HTMLDivElement>;
}) {
  if (!customisableFit) return null;

  const { parseContent } = useRichText();
  const content = parseContent(customisableFit.content);

  const detailFitKitSections = [
    {
      title: "A little too roomy?",
      description:
        "Place the forefoot padding under the footbed for a snugger fit.",
      icon: <IconForefootPadding className="w-[90px] md:w-[100px]" />,
    },
    {
      title: "Too snug?",
      description:
        "Swap the original footbed with the thin footbeds for extra wiggle room.",
      icon: <IconThinFootbed className="w-[90px] md:w-[100px]" />,
    },
    {
      title: "Have orthotics?",
      description:
        "Remove our footbed and insert your orthotics for all-day comfort that's tailored to you.",
      icon: <IconOrthotics className="w-[90px] md:w-[100px]" />,
    },
  ];

  return (
    <div
      ref={wrapperRef}
      className="flex flex-col space-y-mini px-midi md:px-maxi"
    >
      {content}

      <div className="grid grid-cols-1 gap-mini md:grid-cols-2">
        <SanityImage
          data={customisableFit.fitkitImage.asset}
          altText={customisableFit.fitkitImage.alt ?? ""}
          maxWidth="100vw"
        />

        <div className="rte flex flex-col space-y-mini">
          {detailFitKitSections.map((section, index) => {
            return (
              <div className="flex flex-col space-y-micro">
                {section.icon}
                <h5>{section.title}</h5>
                <p className="text-hint">{section.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col space-y-mini pb-midi">
        <p className="text-hint">Watch how to use the Fit Kit (1 Minute)</p>
        <div className="relative w-full pt-[56.25%]">
          <div className="inset-0 absolute">
            <Video
              url="https://player.vimeo.com/video/583714820"
              playing={false}
              controls={true}
              loop={false}
              muted={false}
              style={{
                objectFit: "cover",
              }}
              width="100%"
              height="100%"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function BiomechanicalFootbedSheetContent({
  biomechanicalFootbed,
  wrapperRef,
  gender,
}: Pick<SizeAndFitProps, "gender"> & {
  biomechanicalFootbed: ServerDataSuccess["biomechanicalFootbedSheetContents"];
  wrapperRef: RefObject<HTMLDivElement>;
}) {
  if (!biomechanicalFootbed) return null;

  const { parseContent } = useRichText();
  const content = parseContent(biomechanicalFootbed.content);
  const desktopImage =
    gender === "Mens"
      ? biomechanicalFootbed.footbedImageMenDesktop
      : biomechanicalFootbed.footbedImageWomenDesktop;
  const mobileImage =
    gender === "Mens"
      ? biomechanicalFootbed.footbedImageMenMobile
      : biomechanicalFootbed.footbedImageWomenMobile;

  const detailFootbedSections = [
    {
      title: "Metatarsal Dome",
      description:
        "Aims to prevent discomfort by reducing pressure in the ball of the foot.",
      icon: <IconMetatarsalDome className="w-[80px]" />,
    },
    {
      title: "Cuboid Notch",
      description:
        "Helps to stabilise your foot to promote better foot function.",
      icon: <IconCuboidNotch className="w-[80px]" />,
    },
    {
      title: "Midfoot Arch Support",
      description: "Provides cushioned support for optimal comfort.",
      icon: <IconArchSupport className="w-[80px]" />,
    },

    {
      title: "Heel Cup",
      description:
        "Cushions the heel, providing shock absorption to aid in the prevention of heel pain.",
      icon: <IconHeelCup className="w-[80px]" />,
    },
  ];

  return (
    <div
      ref={wrapperRef}
      className="flex flex-col space-y-mini px-midi md:px-maxi"
    >
      {content}

      <div>
        {desktopImage ? (
          <SanityImage
            className={clsx(!!mobileImage ? "hidden" : "flex", "md:flex")}
            data={desktopImage.asset}
            altText={desktopImage.alt ?? ""}
            maxWidth="100vw"
          />
        ) : null}
        {mobileImage ? (
          <SanityImage
            className="md:hidden"
            data={mobileImage.asset}
            altText={mobileImage.alt ?? ""}
            maxWidth="100vw"
          />
        ) : null}
      </div>

      <div className="rte grid grid-cols-2 gap-mini pb-midi md:grid-cols-4">
        {detailFootbedSections.map((section, index) => {
          return (
            <div className="flex flex-col space-y-pico">
              {section.icon}
              <h5>
                {index + 1}. {section.title}
              </h5>
              <p className="text-hint">{section.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface TableControlsProps {
  scrollRight: () => void;
  scrollLeft: () => void;
  canScrollRight: boolean;
  canScrollLeft: boolean;
}

function TableControls({
  scrollRight,
  scrollLeft,
  canScrollRight,
  canScrollLeft,
}: TableControlsProps) {
  return (
    <div className="mb-micro flex justify-end space-x-micro">
      <button
        disabled={!canScrollLeft}
        onClick={() => scrollLeft()}
        aria-label="Previous"
        aria-disabled={!canScrollLeft}
        className={clsx(
          "focus-visible:outline-dashed focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-current",
          !canScrollLeft && "opacity-50",
        )}
      >
        <IconVintageArrow className="w-[20px] text-primary" direction="left" />
      </button>
      <button
        aria-label="Next"
        disabled={!canScrollRight}
        onClick={() => scrollRight()}
        aria-disabled={!canScrollRight}
        className={clsx(
          "focus-visible:outline-dashed focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-current",
          !canScrollRight && "opacity-50",
        )}
      >
        <IconVintageArrow className="w-[20px] text-primary" direction="right" />
      </button>
    </div>
  );
}
